import { AbsenceForm } from "@/forms/absence-form";
import { client } from "@/hono";
import { useAbsenceEditorRole } from "@/hooks/use-absence-editor-role";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";
import { Button } from "@msuite/ui/button";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, Modal, type ModalProps, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { getIsAbsenceEditable } from "@msuite/utils/absences";
import { type AbsenceFormSchema, absenceFormSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import type { AbsenceListItem } from "@msuite/utils/types";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditAbsenceModalProps extends ModalProps {
	absenceId: string;
}

export function EditAbsenceModal({ isOpen, onClose, absenceId }: EditAbsenceModalProps) {
	/** State */
	// The loaded absence's date and category, kept so the editability rule is evaluated at render
	// time against the caller's role rather than once while the form was loading.
	const [absence, setAbsence] = useState<Pick<AbsenceListItem, "startDate" | "category"> | null>(
		null,
	);

	/** Hooks */
	const auth = useAuth();
	const { t } = useTranslation();
	const qc = useQueryClient();
	const asyncModal = useAsyncModal();
	const { citadel } = useCitadel();
	const editorRole = useAbsenceEditorRole();

	/** Hooks */
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.users[":userId"].absences[":absenceId"].$get({
			param: {
				userId: guardString(auth.user?.id),
				absenceId,
			},
		});
		const data = await responseHandler(response);
		setAbsence(data.absence);

		return absenceFormSchema.parse(data.absence);
	});
	const methods = useForm({
		resolver: resolver(absenceFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: AbsenceFormSchema) {
		const response = await client.users[":userId"].absences[":absenceId"].$patch({
			param: {
				userId: guardString(auth.user?.id),
				absenceId,
			},
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({
			predicate: invalidate(["absences", "absence"]),
		});
		onClose();
	}

	async function handleOnDeleteAbsence() {
		toast.promise(
			async () => {
				const isConfirmed = await asyncModal.confirm();
				if (!isConfirmed) return;
				const response = await client.users[":userId"].absences[":absenceId"].$delete({
					param: {
						userId: guardString(auth.user?.id),
						absenceId,
					},
				});
				await responseHandler(response);
				await qc.invalidateQueries({
					predicate: invalidate(["absences", "absence"]),
				});
				onClose();
			},
			{
				loading: t("general.deleting"),
				success: t("general.successfully_deleted"),
			},
		);
	}

	/** Computed */
	// Two independent conditions guard the footer: `isEditable` is the business
	// rule about the absence itself — an employee withdraws their own vacation
	// request only while it has not started, an administrator any absence — and
	// `canDeleteAbsence` is the `delete Absence` rule the delete route checks.
	// Without the rule the button is absent, not merely disabled.
	const isEditable = getIsAbsenceEditable(absence, editorRole);
	const canDeleteAbsence = citadel?.can("delete", "Absence") === true;

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				title={t("absences.edit_absence")}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<AbsenceForm />
						</Modal.Body>
						<Modal.FooterForm>
							{canDeleteAbsence && (
								<Button
									variant="danger-ghost"
									onClick={handleOnDeleteAbsence}
									isDisabled={!isEditable}
								>
									{t("general.delete")}
								</Button>
							)}
						</Modal.FooterForm>
					</>
				)}
			</Modal>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("absences.delete_absences_confirm_message")}
				/>
			)}
		</FormProvider>
	);
}
